import React, { useEffect, useState } from 'react';
import { toast } from '../../../../../../../ui/toast';
import {
  useGetPreQualConfigQuery,
  useUpdatePreQualQuestionsMutation,
} from '../../../../../../../utils/api_service/preQualApi';
import {
  PreQualQuestion,
} from '../../../../../../../utils/types/JobOpenings';

export const usePreQualifyingQuestions = (jobOpeningId: string) => {
  const { data: config, isLoading } = useGetPreQualConfigQuery(jobOpeningId);
  const [updateQuestions] = useUpdatePreQualQuestionsMutation();

  const [questions, setQuestions] = useState<PreQualQuestion[]>([]);
  const [newQuestionType, setNewQuestionType] =
    useState<PreQualQuestion['questionType']>('number');
  const [newQuestionText, setNewQuestionText] = useState<string>('');
  const [choices, setChoices] = useState<string[]>(['']);
  const [editQuestionType, setEditQuestionType] =
    useState<PreQualQuestion['questionType']>('number');
  const [editQuestionText, setEditQuestionText] = useState<string>('');
  const [editChoices, setEditChoices] = useState<string[]>(['']);
  const [editIndex, setEditIndex] = useState<string | null>(null);

  useEffect(() => {
    if (config?.questions) {
      const repairedQuestions = config.questions.map((q) => ({
        ...q,
        questionKey: (q.questionKey && q.questionKey !== '') ? q.questionKey : generateUniqueKey(),
      }));
      setQuestions(repairedQuestions);
    }
  }, [config]);

  const generateUniqueKey = () => {
    return `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .substring(2)}`;
  };

  const handleAddQuestion = async () => {
    if (!newQuestionText.trim()) return;

    const filteredChoices = choices.filter((choice) => choice.trim() !== '');
    const newQuestion: PreQualQuestion = {
      questionKey: generateUniqueKey(),
      questionType: newQuestionType,
      question: newQuestionText,
      choices: newQuestionType === 'multiple-choice' ? [...filteredChoices] : [],
      order: questions.length,
    };

    const updatedQuestions = [...questions, newQuestion];

    try {
      await updateQuestions({ jobOpeningId, questions: updatedQuestions }).unwrap();
      setNewQuestionText('');
      setChoices(['']);
      toast.success('Question added successfully');
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  const handleChoiceChange = (index: number, value: string, edit: boolean) => {
    if (edit) {
      const newChoices = [...editChoices];
      newChoices[index] = value;
      setEditChoices(newChoices);
    } else {
      const newChoices = [...choices];
      newChoices[index] = value;
      setChoices(newChoices);
    }
  };

  const handleDeleteQuestion = async (key: string) => {
    const updatedQuestions = questions
      .filter((q) => q.questionKey !== key)
      .map((q, index) => ({ ...q, order: index }));

    try {
      await updateQuestions({ jobOpeningId, questions: updatedQuestions }).unwrap();
      toast.info('Question removed');
    } catch (error) {
      toast.error('Failed to remove question');
    }
  };

  const handleSaveQuestion = async () => {
    if (editIndex !== null) {
      const updatedQuestions = questions.map((question) => {
        if (question.questionKey === editIndex) {
          return {
            ...question,
            question: editQuestionText,
            questionType: editQuestionType,
            choices: editQuestionType === 'multiple-choice'
              ? editChoices.filter((choice) => choice.trim() !== '')
              : [],
          };
        }
        return question;
      });

      try {
        await updateQuestions({ jobOpeningId, questions: updatedQuestions }).unwrap();
        setEditIndex(null);
        setEditQuestionText('');
        setEditChoices(['']);
        toast.success('Question updated');
      } catch (error) {
        toast.error('Failed to update question');
      }
    }
  };

  const handleEditQuestion = (question: PreQualQuestion) => {
    if (question.questionKey) {
      setEditIndex(question.questionKey);
      setEditQuestionText(question.question);
      setEditQuestionType(question.questionType);
      setEditChoices(question.choices.length > 0 ? question.choices : ['']);
    }
  };

  const handleAddFromBank = async (bankQuestion: {
    question: string;
    questionType:
      | 'numeric'
      | 'multiple-choice'
      | 'location'
      | 'text'
      | 'video-answer'
      | 'video';
    choices?: string[];
  }) => {
    if (bankQuestion.questionType === 'text') {
      toast.error('Text questions are not supported in pre-qualifying questions');
      return;
    }

    const questionType =
      bankQuestion.questionType === 'numeric'
        ? 'number'
        : bankQuestion.questionType === 'video'
          ? 'video-answer'
          : bankQuestion.questionType;

    const newQuestion: PreQualQuestion = {
      questionKey: generateUniqueKey(),
      question: bankQuestion.question,
      questionType,
      choices: bankQuestion.choices || [],
      order: questions.length,
    };

    const updatedQuestions = [...questions, newQuestion];

    try {
      await updateQuestions({ jobOpeningId, questions: updatedQuestions }).unwrap();
      toast.success('Question imported from bank');
    } catch (error) {
      toast.error('Failed to import question');
    }
  };

  return {
    choices,
    editChoices,
    setNewQuestionType,
    handleSaveQuestion,
    handleEditQuestion,
    handleDeleteQuestion,
    handleChoiceChange,
    handleAddQuestion,
    handleAddFromBank,
    setNewQuestionText,
    setEditChoices,
    editQuestionType,
    setEditQuestionType,
    editQuestionText,
    setEditQuestionText,
    editIndex,
    questions,
    newQuestionText,
    newQuestionType,
    setChoices,
    setEditIndex,
    isLoading,
  };
};
